import { createContext, useEffect, useState, useRef, useMemo } from "react";
import TrackingManager from "../utils/TrackingManager";
import PostHogService from "@core/services/analytics/PostHogService";
import GoogleAnalyticsService from "@core/services/analytics/GoogleAnalyticsService";

// Create the tracking context
const TrackingContext = createContext({
  trackEvent: () => {},
  setUserProperties: () => {},
  resetUser: () => {},
});

// Directly use environment variables from import.meta.env (Vite's way)
const ENV = {
  POSTHOG_KEY:
    import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY ||
    "phc_qAIIiqxjxtPaHS5NFAK0oGy0ELftQPa4siqzGK51kUk",
  POSTHOG_HOST:
    import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST ||
    "https://us.i.posthog.com",
  GA_ID: import.meta.env.VITE_REACT_APP_GOOGLE_ANALYTICS_ID || "G-GFRQWGZ27T", // From index.html
};

/**
 * TrackingProvider component that initializes the tracking services
 * and provides tracking methods via context
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} [props.services] - Which services to enable ('posthog' and/or 'google_analytics')
 * @param {Object} [props.config] - Configuration for the services
 * @param {Object} [props.config.posthog] - PostHog configuration
 * @param {Object} [props.config.google_analytics] - Google Analytics configuration
 * @param {Object} [props.debug] - Debug options
 * @param {boolean} [props.debug.enabled=false] - Whether debug mode is enabled
 * @returns {React.ReactElement} The provider component
 */
export function TrackingProvider({
  children,
  services = { posthog: true, google_analytics: true },
  config = {},
  debug = { enabled: false },
}) {
  const [trackingManager, setTrackingManager] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initAttempted = useRef(false);

  useEffect(() => {
    // Prevent duplicate initialization attempts
    if (initAttempted.current) return;
    initAttempted.current = true;

    // Create the tracking manager with debug options
    const manager = new TrackingManager({ debug });

    // Register enabled services
    if (services.posthog) {
      manager.registerService("posthog", new PostHogService());
    }

    if (services.google_analytics) {
      manager.registerService("google_analytics", new GoogleAnalyticsService());
    }

    setTrackingManager(manager);

    // Initialize services
    const initializeServices = async () => {
      // Prepare configuration
      const serviceConfig = {};

      // PostHog configuration
      if (services.posthog) {
        serviceConfig.posthog = {
          apiKey: config.posthog?.apiKey || ENV.POSTHOG_KEY,
          apiHost: config.posthog?.apiHost || ENV.POSTHOG_HOST,
          capturePageview: config.posthog?.capturePageview,
          autocapture: config.posthog?.autocapture || false,
        };
      }

      // Google Analytics configuration
      if (services.google_analytics) {
        serviceConfig.google_analytics = {
          measurementId: config.google_analytics?.measurementId || ENV.GA_ID,
        };
      }

      // Initialize the manager with the configuration
      try {
        await manager.initialize(serviceConfig);
        setIsInitialized(true);

        if (debug.enabled) {
          console.log(
            "[TrackingProvider] Tracking services initialized successfully"
          );
        }
      } catch (error) {
        console.error(
          "[TrackingProvider] Error initializing tracking services:",
          error
        );
      }
    };

    initializeServices();

    // Cleanup
    return () => {
      if (debug.enabled) {
        console.log("[TrackingProvider] Cleaning up tracking services");
      }
      // No specific cleanup needed for now
    };
  }, []); // Empty dependency array to run only once

  // Methods to expose via context
  const trackEvent = async (event: {
    name: string;
    params?: Record<string, any>;
  }) => {
    if (trackingManager && isInitialized) {
      return trackingManager.trackEvent(event);
    } else if (debug.enabled) {
      console.warn(
        "[TrackingProvider] trackEvent called before initialization",
        event
      );
    }
  };

  const setUserProperties = async (properties) => {
    if (trackingManager && isInitialized) {
      return trackingManager.setUserProperties(properties);
    } else if (debug.enabled) {
      console.warn(
        "[TrackingProvider] setUserProperties called before initialization",
        properties
      );
    }
  };

  const resetUser = async () => {
    if (trackingManager && isInitialized) {
      return trackingManager.resetUser();
    } else if (debug.enabled) {
      console.warn("[TrackingProvider] resetUser called before initialization");
    }
  };

  // Set the context value with tracking methods
  const contextValue = {
    trackEvent,
    setUserProperties,
    resetUser,
    isInitialized,
  };

  const memoizedContextValue = useMemo(() => {
    return contextValue;
  }, [contextValue]);

  return (
    <TrackingContext.Provider value={memoizedContextValue}>
      {children}
    </TrackingContext.Provider>
  );
}

export default TrackingContext;

